import {
  IonAccordion,
  IonAccordionGroup,
  IonLabel,
  IonRouterContext,
} from '@ionic/react';
import { Formik, FormikProps } from 'formik';
import { cloneDeep } from 'lodash';
import {
  ReactElement,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '../../api/authorization.provider';
import {
  Chart,
  GetChartDataDocument,
  ListChartsDocument,
  ListChartsQuery,
  useCreateChartMutation,
  useGetChartQuery,
  useGetChartTemplateQuery,
  useRemoveChartMutation,
  useUpdateChartMutation,
} from '../../api/graphql/generated';
import { useCurrentUserProp } from '../../api/user.hooks';
import { trackCreateChart } from '../../config/analytics';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { useRedirect } from '../../pages/redirect.hooks';
import { useBeaconRouter } from '../../route/route.hooks';
import { logger } from '../../util/logger';
import { IonFormSwitchField } from '../form/ion-form-switch.field';
import { BeaconForm, FormData } from '../forms/beacon-form';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { IonItem } from '../ionic/IonItem';
import { useSaveStatusToast } from '../toast/toast.gql';

interface ConfigureChartProps {
  context: TeamChartTemplateContext;
  chartId?: string;
  templateId?: string | null;
}

interface TeamChartTemplateContext {
  teamId: string;
}

export interface ChartUiFields {
  hideZeroValues?: boolean;
}

const ConfigureChart = (props: ConfigureChartProps): ReactElement => {
  const { context, chartId, templateId } = props;
  const redirect = useRedirect();
  const ionRouterContext = useContext(IonRouterContext);

  const { builder } = useBeaconRouter();

  const userId = useCurrentUserProp<string>('id');

  const hasDeletePermission = useHasPermission('delete', 'chart', {
    parentId: context.teamId,
    createdBy: userId,
  });

  const { data, loading, error, refetch } = useGetChartQuery({
    variables: { input: { id: chartId ?? '' } },
    skip: typeof chartId !== 'string',
  });

  const { data: templateGqlData } = useGetChartTemplateQuery({
    variables: {
      input: {
        id: templateId as string,
      },
    },
    skip: templateId === undefined,
  });

  const chartUiFieldsRef = useRef<ChartUiFieldsRef | undefined>(undefined);

  const [removeChart, { error: removeError, loading: removeLoading }] =
    useRemoveChartMutation();

  const [createChart, { loading: createLoading, error: createError }] =
    useCreateChartMutation();
  const [updateChart, { loading: updateLoading, error: updateError }] =
    useUpdateChartMutation();

  const onRemove = async () => {
    if (chartId) {
      await removeChart({
        variables: { input: { id: chartId } },
        update: (cache) => {
          const cacheData = cache.readQuery<ListChartsQuery>({
            query: ListChartsDocument,
            variables: { input: { parentId: context.teamId } },
          });
          const items: Chart[] = [
            ...(cacheData?.listCharts.items ?? []),
          ].filter((c) => c.id !== chartId);
          cache.writeQuery({
            query: ListChartsDocument,
            variables: { input: { parentId: context.teamId } },
            data: {
              listCharts: {
                ...cacheData?.listCharts,
                items,
                total: items.length,
              },
            },
          });
        },
      });
      logger.info('removed chart', { id: chartId });
      ionRouterContext.push(builder.getTeamRoute(context.teamId), 'back');
    }
  };

  const onFormSubmit = async (formData: FormData): Promise<void> => {
    const chartUiFields = chartUiFieldsRef.current?.getFields();

    const extendedFormData = cloneDeep(formData);

    // we extend the form data with some custom ui only fields
    if (chartUiFields) {
      extendedFormData.ui = chartUiFields;
    }

    if (chartId) {
      await updateChart({
        variables: {
          input: {
            id: chartId,
            displayOrder: 1,
            fields: extendedFormData,
            parentId: context.teamId,
            templateId,
          },
        },
        refetchQueries: [
          {
            query: GetChartDataDocument,
            variables: {
              input: {
                id: chartId,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              },
            },
          },
        ],
      });
      // after saving an existing chart, take the user back to the previous
      // page as a convenience
      const canGoBack = ionRouterContext.canGoBack();
      if (canGoBack) {
        ionRouterContext.back();
      }
    } else {
      await createChart({
        variables: {
          input: {
            displayOrder: 1,
            fields: extendedFormData,
            parentId: context.teamId,
            templateId,
          },
        },
        update: (cache, { data: listChartsData }) => {
          const cacheData = cache.readQuery<ListChartsQuery>({
            query: ListChartsDocument,
            variables: { input: { parentId: context.teamId } },
          });
          const items: Chart[] = [...(cacheData?.listCharts.items ?? [])];
          if (listChartsData) {
            items.push(listChartsData?.createChart);
          }
          cache.writeQuery({
            query: ListChartsDocument,
            variables: { input: { parentId: context.teamId } },
            data: {
              listCharts: {
                ...cacheData?.listCharts,
                items,
                total: items.length,
              },
            },
          });
        },
      });
      trackCreateChart();
    }

    redirect();
  };

  const formId =
    data?.getChart.template?.form.id ??
    templateGqlData?.getChartTemplate.form.id;
  const initialValues = data?.getChart.fields ?? {};

  const canDeleteChart = chartId ? hasDeletePermission : false;

  useSaveStatusToast({
    error: createError,
    loading: createLoading,
  });

  useSaveStatusToast({
    error: updateError,
    loading: updateLoading,
  });

  useSaveStatusToast({
    error: removeError,
    loading: removeLoading,
  });

  return (
    <LoadingRetry
      loading={loading}
      error={error}
      refetch={refetch}
      results={{
        hasResults: formId !== undefined,
        noResultsComponent: <div />,
      }}
    >
      <BeaconForm
        id={formId ?? ''}
        initialData={initialValues}
        onFormSubmit={onFormSubmit}
        context={context as unknown as Record<string, unknown>}
        canDelete={canDeleteChart}
        onFormDelete={() => {
          onRemove();
        }}
        customFormElement={
          <ChartUiFields
            ref={chartUiFieldsRef}
            initialUiFields={initialValues.ui}
          />
        }
      />
    </LoadingRetry>
  );
};

interface ChartUiFieldsProps {
  initialUiFields?: ChartUiFields;
}

interface ChartUiFieldsRef {
  getFields: () => ChartUiFields;
}

const ChartUiFields = forwardRef<
  ChartUiFieldsRef | undefined,
  ChartUiFieldsProps
>((props: ChartUiFieldsProps, ref) => {
  const { initialUiFields } = props;
  const { i18n } = useTranslation();

  const formikRef = useRef<FormikProps<ChartUiFields> | null>(null);

  const initialValues = useMemo<ChartUiFields>(() => {
    return (
      initialUiFields ?? {
        hideZeroValues: false,
      }
    );
  }, [initialUiFields]);

  const onSubmit = useCallback((_form: ChartUiFields) => {
    // nothing to do here since we aren't the primary form, but formik
    // does require that we provide one
  }, []);

  useImperativeHandle(
    ref,
    () => {
      return {
        getFields: () => {
          return formikRef.current?.values ?? initialValues;
        },
      };
    },
    [initialValues]
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      innerRef={(instance) => (formikRef.current = instance)}
    >
      <IonAccordionGroup>
        <IonAccordion>
          <IonItem slot="header">
            <IonLabel>{getLocalizedText('common:advanced_settings')}</IonLabel>
          </IonItem>
          <div slot="content">
            <IonFormSwitchField
              label={i18n.t('common:chart_hide_zero_values')}
              name="hideZeroValues"
            />
          </div>
        </IonAccordion>
      </IonAccordionGroup>
    </Formik>
  );
});

ChartUiFields.displayName = 'ChartUiFields';

export { ConfigureChart };
